/** Food: computed day-type averages, every option in every slot with ingredients and
 *  macros, and a shopping list generated from the rotation. Section 7. */
import { useMemo, useState } from 'react';
import type { StoreApi } from './useStore.ts';
import { PLAN, META } from '../plan/data.ts';
import { dayPlan, isTrainingDay, rotationFrequency } from '../plan/nutrition.ts';
import type { SlotName } from '../plan/types.ts';

const SLOT_LABELS: Record<SlotName, string> = {
  breakfast: 'Breakfast', am: '10:30 snack', lunch: 'Lunch',
  pm: '16:00 snack', dinner: 'Dinner', post: 'Post-training',
};

export function Food({ api }: { api: StoreApi }) {
  const { state } = api;
  const [tab, setTab] = useState<'options' | 'shopping'>('options');

  // Averages are COMPUTED from the rotation, never asserted.
  const averages = useMemo(() => {
    let tK = 0, tP = 0, tF = 0, tC = 0, tN = 0;
    let lK = 0, lP = 0, lF = 0, lC = 0, lN = 0;
    for (let d = 0; d < 112; d++) {
      const p = dayPlan(state, d);
      if (isTrainingDay(state, d)) { tK += p.k; tP += p.p; tF += p.f; tC += p.c; tN++; }
      else { lK += p.k; lP += p.p; lF += p.f; lC += p.c; lN++; }
    }
    return {
      training: { k: tK / tN, p: tP / tN, f: tF / tN, c: tC / tN, n: tN },
      lighter: { k: lK / lN, p: lP / lN, f: lF / lN, c: lC / lN, n: lN },
      mean: { k: (tK + lK) / 112, p: (tP + lP) / 112, f: (tF + lF) / 112 },
    };
  }, [state]);

  const shopping = useMemo(() => {
    const freq = rotationFrequency(state);
    return [...freq.entries()]
      .map(([key, n]) => {
        const [slot, name] = [key.slice(0, key.indexOf(':')), key.slice(key.indexOf(':') + 1)];
        const item = PLAN.food[slot as SlotName].find((f) => f.n === name)!;
        return { slot: slot as SlotName, name, n, d: item.d };
      })
      .sort((a, b) => b.n - a.n || a.slot.localeCompare(b.slot));
  }, [state]);

  return (
    <>
      <h1>Food</h1>
      <p className="sub">
        Every figure below is summed from ingredient lists. There is no target number
        anywhere — the day's calories are whatever the assigned meals add up to.
      </p>

      <div className="card">
        <div className="row">
          <span className="k">Training days ({averages.training.n})</span>
          <span className="v">{Math.round(averages.training.k)} kcal · {Math.round(averages.training.p)} g P · {Math.round(averages.training.f)} g F</span>
        </div>
        <div className="row">
          <span className="k">Lighter days ({averages.lighter.n})</span>
          <span className="v">{Math.round(averages.lighter.k)} kcal · {Math.round(averages.lighter.p)} g P · {Math.round(averages.lighter.f)} g F</span>
        </div>
        <div className="row">
          <span className="k">Mean across the plan</span>
          <span className="v">{Math.round(averages.mean.k)} kcal · {Math.round(averages.mean.p)} g P · {Math.round(averages.mean.f)} g F</span>
        </div>
        <div className="row">
          <span className="k">Protein per kg</span>
          <span className="v">{(averages.mean.p / (META.subject.startWeightKg as number)).toFixed(2)} g/kg</span>
        </div>
        <div className="row">
          <span className="k">Fat as % of calories</span>
          <span className="v">{Math.round(averages.mean.f * 9 / averages.mean.k * 100)}%</span>
        </div>
      </div>

      <div className="segbar">
        <button aria-current={tab === 'options' ? 'page' : undefined}
                onClick={() => setTab('options')}>All options</button>
        <button aria-current={tab === 'shopping' ? 'page' : undefined}
                onClick={() => setTab('shopping')}>How often</button>
      </div>

      {tab === 'options' ? (
        META.slots.map((slot) => (
          <section key={slot}>
            <h2>{SLOT_LABELS[slot]}
              {slot === 'post' && <span className="dim"> · lifting and running days only</span>}
            </h2>
            {PLAN.food[slot].map((m, i) => (
              <div className="card" key={i}>
                <div className="ex-head">
                  <div className="ex-name">{m.n}</div>
                  <div className="ex-reps">{m.k} kcal</div>
                </div>
                <p className="coach" style={{ marginBottom: 6 }}>{m.d}</p>
                <div className="dim" style={{ fontSize: 12.5 }}>
                  {m.p} g protein · {m.f} g fat · {m.c} g carb · {m.t}
                </div>
              </div>
            ))}
          </section>
        ))
      ) : (
        <>
          <p className="sub">
            How often each option comes up across the whole 16-week rotation, so you can see
            what is worth buying in bulk. For an actual list of ingredients to buy, with
            quantities, use the <strong>Shop</strong> tab.
          </p>
          {shopping.map((s) => (
            <div className="card" key={`${s.slot}:${s.name}`}>
              <div className="ex-head">
                <div className="ex-name">{s.name}</div>
                <div className="ex-reps">{s.n}×</div>
              </div>
              <div className="dim" style={{ fontSize: 12.5, marginTop: 4 }}>
                {SLOT_LABELS[s.slot]} · {s.d}
              </div>
            </div>
          ))}
        </>
      )}
    </>
  );
}
